import { useMutation, useQueryClient } from "react-query";
import ItemMenu from "../../../../../components/menus/ItemMenu";
import DueDate from "./DueDate";
import ApiRequest from "../../../../../api/ApiRequest";
import { API_AUTH_TYPES, API_ROUTES } from "../../../../../api/apiConstants";
import { ArchiveIcon, CalendarIcon, StarIcon, TemplateIcon, TrashIcon, InformationCircleIcon, ClockIcon, ChevronDoubleUpIcon, ChevronDoubleDownIcon } from "@heroicons/react/outline";
import TaskContent from "./TaskContent";
import { DateTime } from "luxon";
import DisplayTaskRichText from "./DisplayTaskRichText";
import { classNames } from './../../../../../components/misc/styling';
import { dateFormatter } from "../../../../../helpers/formatters";
import DraggableItem from "../../../../../components/drag-drop/DraggableItem";
import DropTarget from "../../../../../components/drag-drop/DropTarget";
import { MdOutlineDragIndicator } from "react-icons/md";
import ConfettiBox from "../../../../../components/animations/ConfettiBox";
import AlertError from "../../../../../components/alerts/AlertError";
import { StarIcon as StarIconSolid } from "@heroicons/react/solid";
import { useState } from "react";
import { STATUS_OPTIONS } from "../../../../../constants";
import RepeatFrequency from "./RepeatFrequency";
import RepeatUntil from "./RepeatUntil";
import VisibleOn from "./VisibleOn";
import isVisibleNowFunction from "./isVisibleNowFunction";
import { TbClockPlus } from "react-icons/tb";
import { TaskTagList } from "../../../../../components/task-tags";

/**
 * Individual task row used in both the advisor Financial Story and the
 * client portal. Handles task status toggling (complete/incomplete),
 * starring, archiving, drag-and-drop reordering, and inline editing of
 * due date, repeat frequency, repeat-until date, and visible-on date.
 * In meeting mode, edit controls are hidden.
 *
 * @param {object}  props
 * @param {object}  props.task                          - Task data object
 * @param {string}  props.tasksListQueryKey             - React-Query key for the tasks list
 * @param {string}  props.chaptersListQueryKey          - React-Query key for the chapters list
 * @param {Array}   [props.additionalQueries=[]]        - Extra query keys to invalidate on mutation
 * @param {boolean} [props.isClientPortalTask=false]    - Uses user auth and client-portal styling when true
 * @param {boolean} [props.isMeetingMode]               - Hides edit controls in meeting mode
 * @param {string}  [props.completedTasksListQueryKey]  - Query key for the completed tasks list
 * @param {object}  [props.queryKeys]                   - Map of additional query keys
 * @param {boolean} [props.isTruncated=false]           - Renders task content with expand/collapse toggle
 * @param {string}  [props.query]                       - Search term forwarded to content highlighting
 * @param {boolean} [props.startInEditMode=false]       - Opens the content editor immediately on mount
 */
const Task = ({
    task,
    tasksListQueryKey,
    chaptersListQueryKey,
    additionalQueries = [],
    isClientPortalTask = false,
    isMeetingMode,
    completedTasksListQueryKey,
    queryKeys,
    isTruncated = false,
    query,
    startInEditMode = false,
}) => {
    //hooks
    //state
    //functions
    const tasksApi = new ApiRequest(
        API_ROUTES.CLIENT_TASKS,
        isClientPortalTask ? API_AUTH_TYPES.USER : API_AUTH_TYPES.ADVISOR
    );

    const queryClient = useQueryClient();

    const { mutate: archiveTask } = useMutation(
        (body) => tasksApi.patch({ endpoint: "archive_task", body }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(tasksListQueryKey);
                queryClient.invalidateQueries(chaptersListQueryKey)
                additionalQueries.forEach(query => {
                    queryClient.invalidateQueries(query)
                })
            },
        }
    );

    const { mutate: updateDate } = useMutation(
        (body) => tasksApi.patch({ endpoint: "update_date", body }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    tasksListQueryKey
                );
                additionalQueries.forEach(query => {
                    queryClient.invalidateQueries(query)
                })
            },
        }
    );

    const { mutate: updateRepeatFrequency } = useMutation(
        (body) => tasksApi.patch({ endpoint: "update_repeat_frequency", body }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    tasksListQueryKey
                );
                additionalQueries.forEach(query => {
                    queryClient.invalidateQueries(query)
                })
            },
        }
    );

    const { mutate: updateVisibleOn } = useMutation(
        (body) => tasksApi.patch({ endpoint: "update_visible_on", body }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    tasksListQueryKey
                );
                queryClient.invalidateQueries(chaptersListQueryKey)
                additionalQueries.forEach(query => {
                    queryClient.invalidateQueries(query)
                })
            },
        }
    );

    const { mutate: updateRepeatUntil } = useMutation(
        (body) => tasksApi.patch({ endpoint: "update_repeat_until", body }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    tasksListQueryKey
                );
                additionalQueries.forEach(query => {
                    queryClient.invalidateQueries(query)
                })
            },
        }
    );
    const { mutate: updateTaskOrder } = useMutation(
        (body) => tasksApi.patch({ endpoint: "update_task_order", body }),
        {
            onSuccess: ({ old_chapter_status, new_chapter_status }) => {
                queryClient.invalidateQueries(
                    tasksListQueryKey
                );
                queryClient.invalidateQueries(queryKeys[old_chapter_status]);
                queryClient.invalidateQueries(queryKeys[new_chapter_status]);
            },
        }
    );
    const { mutate: completeTask } = useMutation(
        (body) => tasksApi.patch({ endpoint: "update_task_completed", body }),
        {
            onSuccess: (data) => {
                queryClient.invalidateQueries(
                    tasksListQueryKey
                );

                if (data.chapter_tasks_remaining === 0)
                    queryClient.invalidateQueries(chaptersListQueryKey);
                queryClient.invalidateQueries(completedTasksListQueryKey);
                additionalQueries.forEach(query => {
                    queryClient.invalidateQueries(query)
                })
            },
        }
    );

    const [maxHighlightsError, setMaxHighlightsError] = useState(false);
    const { mutate: toggleIsHighlighted } = useMutation(
        (body) => tasksApi.patch({ endpoint: "toggle_is_highlighted", body }),
        {
            onSuccess: (body) => {
                queryClient.invalidateQueries(
                    tasksListQueryKey
                );
                queryClient.invalidateQueries(chaptersListQueryKey)
                if (body.error) {
                    setMaxHighlightsError(true);
                    setTimeout(() => {
                        setMaxHighlightsError(false);
                    }, 3000);
                }
                additionalQueries.forEach(query => {
                    queryClient.invalidateQueries(query)
                })
            },
        }
    );


    const handleEditDate = async (newValue) => {
        try {
            updateDate({
                task_id: task?.task_id,
                date: newValue,
            });
        } catch (error) {
            console.error('[Task] Operation failed:', error);
        }
    };

    const handleEditRepeatFrequency = async (newValue) => {
        try {
            updateRepeatFrequency({
                task_id: task?.task_id,
                new_value: newValue,
                due_date: task?.due_date,
            });
        } catch (error) {
            console.error('[Task] Operation failed:', error);
        }
    };

    const handleEditRepeatUntil = async (newValue) => {
        try {
            updateRepeatUntil({
                task_id: task?.task_id,
                new_value: newValue,
            });
        } catch (error) {
            console.error('[Task] Operation failed:', error);
        }
    };

    const handleEditVisibleOn = async (newValue) => {
        try {
            updateVisibleOn({
                task_id: task?.task_id,
                new_value: newValue,
            });
        } catch (error) {
            console.error('[Task] Operation failed:', error);
        }
    };

    const handleArchive = async () => {
        try {
            archiveTask({
                task_id: task?.task_id,
            });
        } catch (error) {
            console.error('[Task] Operation failed:', error);
        }
    };

    const handleToggleIsHighlighted = async (e) => {
        // Stop propagation to prevent drag/drop handlers from intercepting the click
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        try {
            toggleIsHighlighted({
                task_id: task?.task_id,
                status: task?.status,
            });
        } catch (error) {
            console.error('[Task] Operation failed:', error);
        }
    };


    const [displayConfetti, setDisplayConfetti] = useState(false);
    const handleCompleteTask = async () => {

        try {
            if (task?.status === STATUS_OPTIONS.TASKS.COMPLETED) {
                completeTask({
                    task_id: task?.task_id,
                    status: STATUS_OPTIONS.TASKS.ACTIVE,
                    chapter_id: task?.chapter_id,
                });
            } else {


                setDisplayConfetti(true);
                setTimeout(() => {
                    setDisplayConfetti(false);
                    completeTask({
                        task_id: task?.task_id,
                        status: STATUS_OPTIONS.TASKS.COMPLETED,
                        chapter_id: task?.chapter_id,
                    });
                }, 3000); // Set this to the same value as the confetti duration
            }


        } catch (error) {
            console.error('[Task] Operation failed:', error);
        }
    };



    const handleTaskReorder = (relativePosition, item) => {
        const droppedTaskId = item.content.props.task?.task_id
        const droppedChapterId = item.content.props.task?.chapter_id
        updateTaskOrder({
            item_id: droppedTaskId,
            dropped_over_id: task?.task_id,
            dropped_over_list_id: task?.chapter_id,
            item_list_id: droppedChapterId,
            item_type: 'task',
            household_id: task?.household_id
        });
    }


    const templateApi = new ApiRequest(API_ROUTES.FINANCIAL_STORY_TEMPLATES, API_AUTH_TYPES.ADVISOR);
    const { mutate: postTemplate } = useMutation(
        (body) => templateApi.post({ endpoint: "post_template", body: { content: task?.content, type: 'task' } }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('task-templates')
            },
        }
    );

    const { mutate: reorderToTop } = useMutation(
        (body) => tasksApi.patch({ endpoint: "reorder_to_top_of_chapter", body }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(tasksListQueryKey);
                additionalQueries.forEach(query => {
                    queryClient.invalidateQueries(query)
                })
            },
        }
    );

    const { mutate: reorderToBottom } = useMutation(
        (body) => tasksApi.patch({ endpoint: "reorder_to_end_of_chapter", body }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(tasksListQueryKey);
                additionalQueries.forEach(query => {
                    queryClient.invalidateQueries(query)
                })
            },
        }
    );

    const handleMoveToTop = () => {
        reorderToTop({
            item_id: task.task_id,
            item_list_id: task.chapter_id,
            dropped_over_list_id: task.chapter_id,
            item_type: 'task',
            household_id: task.household_id,
        });
    };

    const handleMoveToBottom = () => {
        reorderToBottom({
            item_id: task.task_id,
            item_list_id: task.chapter_id,
            dropped_over_list_id: task.chapter_id,
            item_type: 'task',
            household_id: task.household_id,
        });
    };

    const menuOptions = [
        {
            title: task?.status === STATUS_OPTIONS.TASKS.HIGHLIGHTED ? "Unhighlight" : "Highlight",
            action: handleToggleIsHighlighted,
            icon: StarIcon,

        },
        {
            title: "Move to Top",
            action: handleMoveToTop,
            icon: ChevronDoubleUpIcon,
        },
        {
            title: "Move to Bottom",
            action: handleMoveToBottom,
            icon: ChevronDoubleDownIcon,
        },
        {
            title: "Create Template",
            action: postTemplate,
            icon: TemplateIcon,
        }, {
            title: "Delete",
            action: handleArchive,
            icon: TrashIcon,
        },


    ];

    if (!task) return null;
    const isVisibleNow = isVisibleNowFunction(task);

    return (
        <>
            {displayConfetti && <ConfettiBox />}
            <DropTarget accept="task" onDrop={(item) => handleTaskReorder('above', item)} isActiveStyling={'pt-28 drop-target'} >
                <div className="relative w-full h-1">
                    <div className="h-8 absolute w-full ">
                    </div>
                </div>

                <div className={classNames(isMeetingMode && "text-lg", "block border border-gray-200 rounded-lg", isVisibleNow ? 'bg-white' : 'bg-gray-100/50')}>
                    <div className="px-3 py-2.5 relative">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-wrap">
                                <input
                                    type="checkbox"
                                    name=""
                                    id=""
                                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    defaultChecked={task?.status === STATUS_OPTIONS.TASKS.COMPLETED}
                                    onClick={handleCompleteTask}
                                />
                                {isClientPortalTask ?
                                    task?.status === STATUS_OPTIONS.TASKS.HIGHLIGHTED && <StarIconSolid className="h-4 w-4 text-yellow-500" />
                                    : <button type="button" onClick={handleToggleIsHighlighted} className="p-1 -m-1">
                                        {task?.status === STATUS_OPTIONS.TASKS.HIGHLIGHTED ? <StarIconSolid className="h-4 w-4 text-yellow-500" /> : <StarIcon className="h-4 w-4 text-gray-300 hover:text-yellow-500" />}
                                    </button>}
                                {maxHighlightsError && <div className="z-10"> <AlertError> You can only highlight a total of 3 tasks at a time for a household</AlertError></div>}
                                <DueDateComponent isClientPortalTask={isClientPortalTask} task={task} handleEditDate={handleEditDate} />
                                <VisibleOn isClientPortal={isClientPortalTask} task={task} handleSubmitEdit={handleEditVisibleOn} />
                                <RepeatFrequency isClientPortal={isClientPortalTask} task={task} handleSubmitEdit={handleEditRepeatFrequency} />
                                {task.repeat_frequency && <RepeatUntil isClientPortal={isClientPortalTask} task={task} handleSubmitEdit={handleEditRepeatUntil} />}
                                {task?.created_at && !isClientPortalTask && (
                                    <div className="flex items-center text-sm text-gray-400 relative group">
                                        <TbClockPlus className="h-3.5 w-3.5 mr-1" />
                                        <span>{dateFormatter.format(new Date(task.created_at))}</span>
                                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block px-2 py-1 bg-gray-700 text-white text-sm rounded whitespace-nowrap z-20">
                                            Created: {dateFormatter.format(new Date(task.created_at))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {!isClientPortalTask && (
                                <div className="ml-2 flex flex-shrink-0 items-center gap-1">
                                    <DraggableItem
                                        isDraggable={!isClientPortalTask}
                                        type='task'
                                        item={{
                                            type: 'task',
                                            content: <Task
                                                task={task}
                                                tasksListQueryKey={tasksListQueryKey}
                                                chaptersListQueryKey={chaptersListQueryKey}
                                                isMeetingMode={isMeetingMode}
                                                type='task'
                                            />
                                        }}
                                    >
                                        <MdOutlineDragIndicator className="h-4 w-4 text-gray-400" />
                                    </DraggableItem>
                                    <ItemMenu options={menuOptions} />
                                </div>
                            )}
                        </div>
                        <div className="mt-1.5 text-base">
                            {" "}
                            {isClientPortalTask ? (
                                <DisplayTaskRichText task={task} isClientPortalTask={isClientPortalTask} isTruncated={isTruncated} query={query} />
                            ) : (
                                <div className="text-left text-gray-700 w-full z-10">
                                    <TaskContent task={task} listQueryKey={tasksListQueryKey} startInEditMode={startInEditMode} />
                                </div>
                            )}
                        </div>
                        {/* Task Tags */}
                        {(task.tags?.length > 0 || !isClientPortalTask) && (
                            <div className="mt-2">
                                <TaskTagList
                                    taskId={task.task_id}
                                    tags={task.tags || []}
                                    isEditable={!isClientPortalTask}
                                    onTagsChange={() => queryClient.invalidateQueries(tasksListQueryKey)}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </DropTarget>
        </>
    );
};

function DueDateComponent({ isClientPortalTask, task, handleEditDate }) {
    return (
        <>
            {isClientPortalTask ? (
                <div className="flex items-center text-sm text-gray-500">
                    {task?.status === STATUS_OPTIONS.TASKS.COMPLETED ? (
                        <div className="flex items-center gap-1">
                            <span className="text-green-600">✓</span>
                            <span>{dateFormatter.format(new Date(task?.completed_at))}</span>
                        </div>
                    ) : task?.due_date ? (
                        <div className="flex items-center gap-1">
                            <CalendarIcon className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                            <time dateTime={task?.due_date}>
                                {DateTime.fromISO(task?.due_date).toLocaleString()}
                            </time>
                        </div>
                    ) : null}
                </div>
            ) : (
                <div>
                    {task?.status === STATUS_OPTIONS.TASKS.COMPLETED ? (
                        <div className="flex items-center text-sm text-gray-500 gap-1">
                            <span className="text-green-600">✓</span>
                            <span>{dateFormatter.format(new Date(task?.completed_at))}</span>
                        </div>
                    ) : (
                        <DueDate
                            defaultValue={task?.due_date}
                            handleSubmitEdit={handleEditDate}
                        />
                    )}
                </div>
            )}
        </>
    )
}
export default Task; 